import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
import {
  completeWorkOrdersBatch,
  archiveWorkOrdersBatch,
} from "../../../../../../../../../../../api/kehrblatt/work-orders"
import WorkOrderCancelReplanOverlay from "../../../../../../../_shared/_modules/work-order-cancel-replan-overlay"
import ConfirmAppointmentsOverlay from "../../../../../../../_shared/_modules/confirm-appointments-overlay"
import { isConfirmableAppointment } from "../../../../../../../_shared/_logic/appointment-eligibility"
import { buildArchiveWorkOrderApprovalDescription } from "../../../../../../../../../_shared/_logic/components-work-orders-action-tooltips"
import {
  buildBatchActionNotification,
  buildCompleteBatchDescription,
  selectedWorkOrderRows,
} from "../_logic/batchActions"
import type { WorkOrderRow } from "../../../../../../../_shared/_logic/work-order-table-model"
import type { TableActionRunner } from "../../../row-action-commands"
import {
  uniquePropertyCountForWorkOrders,
  validateDemoBatchPlanningPropertyLimit,
} from "../../../../../../../../../_shared/_logic/demo-batch-planning-limit"

export type BatchActionCommandContext = {
  organizationId: string
  actionLoading: boolean
  scopedCanEdit: boolean
  relevantScheduleEventByWorkOrderId: Record<string, ScheduleEvent>
  properties: Property[]
  customers: Customer[]
  contactPoints: ContactPoint[]
  contactOrders: ContactOrder[]
  employees: { id: string; label: string }[]
  workingHoursByUser: Record<string, any>
  planningSettings: PlanningSettings | null
  trialRestrictionsActive?: boolean | null
  runTableAction: TableActionRunner
  onChanged?: () => Promise<void> | void
  onScheduleEventsUpdated?: (events: ScheduleEvent[]) => Promise<void> | void
}

const employeeOptions = (employees: { id: string; label: string }[]) =>
  employees.map((employee) => ({
    label: employee.label,
    value: employee.id,
  }))

const demoBatchActionLimitReason = (
  context: Pick<BatchActionCommandContext, "trialRestrictionsActive">,
  rows: WorkOrderRow[],
): string | null =>
  validateDemoBatchPlanningPropertyLimit(
    uniquePropertyCountForWorkOrders(rows.map((row) => row.workOrder)),
    {
      trialRestrictionsActive: context.trialRestrictionsActive,
    },
  )

export const completeSelectedWorkOrders = async ({
  rows,
  actionLoading,
  scopedCanEdit,
  runTableAction,
  clearSelection,
  onChanged,
}: Pick<
  BatchActionCommandContext,
  "actionLoading" | "scopedCanEdit" | "runTableAction" | "onChanged"
> & {
  rows: unknown[]
  clearSelection: () => void
}) => {
  if (actionLoading || !scopedCanEdit) return

  const selectedRows = selectedWorkOrderRows(rows)

  if (!selectedRows.length) {
    newNotification({
      class: "info",
      html: "Keine Aufträge ausgewählt.",
    })

    return
  }

  await newApproval({
    title: "Aufträge abschließen",
    description: buildCompleteBatchDescription(selectedRows.length),
    approvalLabel: "Ja, Aufträge abschließen",
    declineLabel: "Abbrechen",
    approvalCallback: async () => {
      await runTableAction(async () => {
        try {
          const result = await completeWorkOrdersBatch(selectedRows.map((row) => row.id))
          newNotification(
            buildBatchActionNotification(result, {
              singular: "Auftrag",
              plural: "Aufträge",
              actionText: "abgeschlossen",
              failureText: "Ausgewählte Aufträge konnten nicht abgeschlossen werden.",
            }),
          )
          clearSelection()
          await onChanged?.()
        } catch (err) {
          console.error("work_orders_batch_complete_failed", err)
          const message =
            (err as { message?: string })?.message ??
            "Ausgewählte Aufträge konnten nicht abgeschlossen werden."
          newNotification({ class: "error", html: message })
        }
      })
    },
  })
}

export const archiveSelectedWorkOrders = async ({
  rows,
  actionLoading,
  scopedCanEdit,
  runTableAction,
  clearSelection,
  onChanged,
}: Pick<
  BatchActionCommandContext,
  "actionLoading" | "scopedCanEdit" | "runTableAction" | "onChanged"
> & {
  rows: unknown[]
  clearSelection: () => void
}) => {
  if (actionLoading || !scopedCanEdit) return

  const targetRows = selectedWorkOrderRows(rows).filter((row) => !row.workOrder.archived_at)

  if (!targetRows.length) return

  await newApproval({
    title: "Aufträge archivieren",
    description: buildArchiveWorkOrderApprovalDescription(targetRows.length),
    approvalLabel: "Ja, archivieren",
    declineLabel: "Abbrechen",
    approvalCallback: async () => {
      await runTableAction(async () => {
        try {
          const result = await archiveWorkOrdersBatch(targetRows.map((row) => row.id))
          newNotification(
            buildBatchActionNotification(result, {
              singular: "Auftrag",
              plural: "Aufträge",
              actionText: "archiviert",
              failureText: "Ausgewählte Aufträge konnten nicht archiviert werden.",
            }),
          )
          clearSelection()
          await onChanged?.()
        } catch (err) {
          console.error("work_orders_batch_archive_failed", err)
          const message =
            (err as { message?: string })?.message ??
            "Ausgewählte Aufträge konnten nicht archiviert werden."
          newNotification({ class: "error", html: message })
        }
      })
    },
  })
}

export const openWorkOrderScheduleBatchOverlay = ({
  context,
  title,
  mode,
  rows,
  clearSelection,
  startDate,
}: {
  context: BatchActionCommandContext
  title: string
  mode:
    | "rescheduleAppointments"
    | "createAppointments"
    | "createAnnouncementPlans"
    | "cancelAnnouncementPlans"
  rows: WorkOrderRow[]
  clearSelection?: () => void
  startDate?: string | null
}) => {
  if (!context.organizationId || context.actionLoading || !context.scopedCanEdit) return

  const targetRows = selectedWorkOrderRows(rows)

  if (!targetRows.length) {
    newNotification({
      class: "info",
      html: "Für diese Sammelaktion wurde kein passender Auftrag gefunden.",
    })

    return
  }

  if (mode !== "cancelAnnouncementPlans") {
    const limitReason = demoBatchActionLimitReason(context, targetRows)

    if (limitReason) {
      newNotification({ class: "error", html: limitReason })

      return
    }
  }

  const targetEvents = targetRows
    .map((row) => context.relevantScheduleEventByWorkOrderId[row.id] ?? null)
    .filter((event): event is ScheduleEvent => Boolean(event?.id))
  openOverlay({
    title,
    content: WorkOrderCancelReplanOverlay,
    bodyPadding: "none",
    defaultFullscreen: true,
    closeOnParentOpen: true,
    properties: {
      organizationId: context.organizationId,
      mode,
      events: targetEvents,
      workOrders: targetRows.map((entry) => entry.workOrder),
      sourceRows: targetRows,
      properties: context.properties,
      customers: context.customers,
      contactPoints: context.contactPoints,
      contactOrders: context.contactOrders,
      employees: employeeOptions(context.employees),
      workingHoursByUser: context.workingHoursByUser,
      startDate: startDate ?? null,
      planningSettings: context.planningSettings,
      trialRestrictionsActive: context.trialRestrictionsActive,
      onCompleted: async () => {
        clearSelection?.()
        await context.onChanged?.()
      },
    },
  })
}

export const openCancelReplanDialog = (
  context: BatchActionCommandContext,
  rows: WorkOrderRow[],
  clearSelection?: () => void,
) =>
  openWorkOrderScheduleBatchOverlay({
    context,
    title: "Termine verschieben",
    mode: "rescheduleAppointments",
    rows,
    clearSelection,
  })

export const openCreateAppointmentsDialog = (
  context: BatchActionCommandContext,
  rows: WorkOrderRow[],
  clearSelection?: () => void,
) =>
  openWorkOrderScheduleBatchOverlay({
    context,
    title: "Termine anlegen",
    mode: "createAppointments",
    rows,
    clearSelection,
  })

export const openCreateAnnouncementPlansDialog = (
  context: BatchActionCommandContext,
  rows: WorkOrderRow[],
  clearSelection?: () => void,
) =>
  openWorkOrderScheduleBatchOverlay({
    context,
    title: "Ankündigungsplan anlegen",
    mode: "createAnnouncementPlans",
    rows,
    clearSelection,
  })

export const openCancelAnnouncementPlansDialog = (
  context: BatchActionCommandContext,
  rows: WorkOrderRow[],
  clearSelection?: () => void,
) =>
  openWorkOrderScheduleBatchOverlay({
    context,
    title: "Ankündigungsplan beenden",
    mode: "cancelAnnouncementPlans",
    rows,
    clearSelection,
  })

export const openConfirmAppointmentsDialog = (
  context: BatchActionCommandContext,
  rows: WorkOrderRow[],
  clearSelection?: () => void,
) => {
  if (!context.organizationId || context.actionLoading || !context.scopedCanEdit) return

  const targetRows = selectedWorkOrderRows(rows)
  const targetEvents = targetRows
    .map((row) => context.relevantScheduleEventByWorkOrderId[row.id] ?? null)
    .filter(isConfirmableAppointment)

  if (!targetEvents.length) {
    newNotification({
      class: "info",
      html: "Für diese Sammelaktion wurden keine bestätigbaren Termine gefunden.",
    })

    return
  }

  const eventIdSet = new Set(targetEvents.map((event) => event.id))
  const rowsWithEvents = targetRows.filter((row) => {
    const event = context.relevantScheduleEventByWorkOrderId[row.id]

    return Boolean(event?.id && eventIdSet.has(event.id))
  })
  const limitReason = demoBatchActionLimitReason(context, rowsWithEvents)

  if (limitReason) {
    newNotification({ class: "error", html: limitReason })

    return
  }

  openOverlay({
    title: "Termine bestätigen",
    content: ConfirmAppointmentsOverlay,
    bodyPadding: "none",
    defaultFullscreen: true,
    closeOnParentOpen: true,
    properties: {
      organizationId: context.organizationId,
      events: targetEvents,
      workOrders: rowsWithEvents.map((row) => row.workOrder),
      sourceRows: rowsWithEvents,
      properties: context.properties,
      onCompleted: async (updatedEvents: ScheduleEvent[]) => {
        await context.onScheduleEventsUpdated?.(updatedEvents)
        clearSelection?.()
        await context.onChanged?.()
      },
    },
  })
}
